import { isNumber, reduce } from 'lodash/fp';
import { Identified } from '../../../../../types';
import { PriceService } from '../../../../../services';
import { StockCard, StockCardCategory, StockLevel } from '../../../../../generated/graphql';
import { translate } from '../../../../../utils';
import { STOCK_QUANTITY_PRECISION } from '../../../../../services/price-service/constants';
import { HeadCell } from '../../../../../ui-kit';
import { CurrencyCode } from '../../../../../server-types';

export const getAllChecked = (selectedIds: string[], items: Identified[]) => {
    const set = new Set<string>();
    selectedIds.forEach(id => set.add(id));
    let isChecked = true;

    // eslint-disable-next-line no-restricted-syntax
    for (const item of items) {
        if (!set.has(item.id)) {
            isChecked = false;
            break;
        }
    }

    return isChecked;
};

export const getSomeChecked = (selectedIds: string[], items: Identified[]) => {
    const set = new Set<string>();
    selectedIds.forEach(id => set.add(id));
    let isSomeChecked = false;

    // eslint-disable-next-line no-restricted-syntax
    for (const item of items) {
        if (set.has(item.id)) {
            isSomeChecked = true;
            break;
        }
    }

    return isSomeChecked;
};
interface MapStockCardToRowsArgs {
    currentWarehouseId: string;
    priceService: PriceService;
    currencyCode?: CurrencyCode;
}

type StockCardFields = 'id' | 'name' | 'storageUnit' | 'conversionUnit' | 'convertedAmount';
interface StockCardType extends Pick<StockCard, StockCardFields> {
    stockLevels: Pick<StockLevel, 'id'| 'quantity' | 'warehouseId' | 'priceWithoutVat'>[];
    stockCardCategory?: Pick<StockCardCategory, 'name'> | null;
}

export const mapStockCardsToRows = ({
    currentWarehouseId,
    priceService,
    currencyCode,
}: MapStockCardToRowsArgs) => reduce<StockCardType, Row[]>((acc, {
    id,
    name,
    storageUnit,
    conversionUnit,
    convertedAmount,
    stockCardCategory,
    stockLevels,
}) => {
    const categoryName = stockCardCategory?.name || translate('Not assigned') as unknown as string;
    const stockLevel = stockLevels.find(({ warehouseId }) => warehouseId === currentWarehouseId);
    if (stockLevel) {
        const leftOvers = stockLevel.quantity;
        let convertedLeftOvers;
        if (isNumber(convertedAmount)) {
            convertedLeftOvers = priceService.multiplyQuantity(
                leftOvers,
                convertedAmount,
                { precision: STOCK_QUANTITY_PRECISION, onlyPositiveInitialValue: false },
            );
        }

        const priceBuy = stockLevel.priceWithoutVat;

        const priceBuyLocalized = priceService.toLocaleString(priceBuy || 0, currencyCode);

        acc.push({
            id,
            name,
            priceBuy: priceBuyLocalized,
            leftOvers: `${leftOvers} ${storageUnit}`,
            convertedLeftOvers: isNumber(convertedLeftOvers)
                ? `${convertedLeftOvers} ${conversionUnit}`
                : translate('Empty value') as unknown as string,
            warehouseCategory: categoryName,
        });
    }

    return acc;
}, []);

export interface Row {
    id: string;
    name: string;
    leftOvers: string;
    convertedLeftOvers: string;
    warehouseCategory: string;
    priceBuy: string;
}

export const headCells: Array<HeadCell<Row>> = [
    { id: 'name', align: 'left', label: translate('Name') },
    { id: 'leftOvers', align: 'center', label: translate('Leftovers') },
    { id: 'convertedLeftOvers', align: 'center', label: translate('Converted leftovers') },
    { id: 'priceBuy', align: 'center', label: translate('Price buy') },
    { id: 'warehouseCategory', align: 'center', label: translate('Warehouse category') },
];
